import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch, getState } from "../../state/store";
import { selectCreatedTableRowsIds, selectMarkets } from "../../state/selectors";
import { clearCreatedTableRowsIds, pushCreatedTableRowsIds, setLocations } from "../../state/ducks";

import { strCode } from "../../consts";
import { log, notificationService, t } from "../../services";

import {
  DropdownInput,
  FlexContainers,
  Modal,
  SearchInput,
  WrapperContainer
} from "../../components";
import StoresTable from "./components/StoresTable/StoresTable";
import { StoresWrapper, NewButton } from "./styled";

const Stores = ({
  active,
  setActive,
  market,
  onSaveStore = () => {},
  onDeleteStore = () => {},
  isReadOnlyAccess = false
}) => {
  const [storesData, setStoresData] = useState([]);

  const [searchValue, setSearchValue] = useState("");
  const [editId, setEditId] = useState(null);
  const [lastCreatedStore, setLastCreatedStore] = useState(null);

  const userMarkets = selectMarkets()(getState()) ?? [];
  const marketOptions = userMarkets?.map((market) => {
    return { id: market.id, value: market.id };
  });

  const [selectedMarket, setSelectedMarket] = useState(marketOptions[0]);

  const locationMarket = useMemo(() => {
    return market ? market : selectedMarket?.id;
  }, [market, selectedMarket]);

  const locationsByMarket = useSelector((state) => state.configs.locations)[locationMarket];

  useEffect(() => {
    if (!active) {
      setSelectedMarket(marketOptions[0]);
    }
  }, [active]);

  useEffect(() => {
    if (locationsByMarket && lastCreatedStore) {
      const createdStore =
        locationsByMarket.find((location) => location.name === lastCreatedStore.name) || null;

      if (createdStore) {
        dispatch(pushCreatedTableRowsIds([createdStore.id]));
      }

      setLastCreatedStore(null);
    }
  }, [locationsByMarket, lastCreatedStore]);

  useEffect(() => {
    if (locationsByMarket) {
      setStoresData(locationsByMarket);
    } else {
      setStoresData([]);
    }
  }, [locationsByMarket, editId]);

  useEffect(() => {
    setSearchValue("");

    if (editId === -1 && !locationsByMarket?.some((location) => location.id === -1)) {
      const newStore = { id: -1, name: "", address: "", city: "" };

      const newStoreData = [newStore, ...storesData];
      setStoresData(newStoreData);
    }

    if ((editId === null || editId !== -1) && locationsByMarket) {
      dispatch(
        setLocations({ market, locations: locationsByMarket.filter((store) => store.id !== -1) })
      );
    }
  }, [editId]);

  const closeStoresModal = () => {
    const filteredLocations = locationsByMarket?.filter((location) => location.id !== -1) || [];

    dispatch(setLocations({ market, locations: [...filteredLocations] }));

    setEditId(null);
    setStoresData(filteredLocations);
    setActive(false);
  };

  const handleCreateStore = () => {
    setSearchValue("");
    setEditId(-1);
  };

  const handleSaveStore = async (storeData) => {
    try {
      await onSaveStore(editId, storeData, locationMarket);

      setEditId(null);
      setLastCreatedStore(storeData);
    } catch (err) {
      log.error("Stores: Failed to save store", err);

      notificationService.showError({
        title: t(strCode.EDIT_LOCATION_ERROR_TITLE),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });

      throw err;
    }
  };

  // Need to remove highlighting on recently created location by click inside modal (global handling doesn't trigger)
  const removeCreatedTableRows = useCallback((event) => {
    event.stopPropagation();
    const createdTableRowsIds = selectCreatedTableRowsIds()(getState());

    if (createdTableRowsIds.length) {
      dispatch(clearCreatedTableRowsIds());
    }
  }, []);

  return (
    <Modal
      id={"stores-modal"}
      active={active}
      setActive={closeStoresModal}
      title={t(strCode.LOCATIONS_TITLE)}
      closable>
      <StoresWrapper
        Container={FlexContainers.FlexColumn}
        gap={24}
        onClick={removeCreatedTableRows}>
        <WrapperContainer Container={FlexContainers.FlexRow} width="100%" gap={22}>
          <WrapperContainer Container={FlexContainers.FlexRow} width="100%" gap={8}>
            <SearchInput
              id="stores-search"
              value={searchValue}
              setValue={setSearchValue}
              placeholder={t(strCode.SEARCH_LOCATIONS_PLACEHOLDER)}
            />

            {!market && (
              <WrapperContainer width={100}>
                <DropdownInput
                  id="market-selector"
                  placeholder={t(strCode.SELECT_PLACEHOLDER)}
                  options={marketOptions}
                  selectedOption={selectedMarket}
                  onSelectOption={(id) => {
                    setSelectedMarket(marketOptions.find((el) => el.id === id));
                  }}
                />
              </WrapperContainer>
            )}
          </WrapperContainer>

          {!isReadOnlyAccess && (
            <NewButton onClick={handleCreateStore}>{t(strCode.NEW_BTN)}</NewButton>
          )}
        </WrapperContainer>
        <StoresTable
          editId={editId}
          setEditId={setEditId}
          storesData={storesData}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          onSaveStore={handleSaveStore}
          onDeleteStore={(...params) => onDeleteStore(...params, locationMarket)}
          isReadOnlyAccess={isReadOnlyAccess}
        />
      </StoresWrapper>
    </Modal>
  );
};

Stores.propTypes = {
  active: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  market: PropTypes.string,
  onSaveStore: PropTypes.func.isRequired,
  onDeleteStore: PropTypes.func.isRequired,
  isReadOnlyAccess: PropTypes.bool
};

export default Stores;
